import { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { FragmentShaderProps, ShaderUniforms } from './types';
import { defaultVertexShader } from './defaultShaders';

/**
 * FragmentShader component for rendering custom GLSL fragment shaders
 * 
 * @example
 * ```tsx
 * <FragmentShader
 *   fragmentShader={myShaderCode}
 *   uniforms={{ u_speed: { value: 2.0 } }}
 * />
 * ```
 */
export default function FragmentShader({
    fragmentShader,
    vertexShader = defaultVertexShader,
    uniforms: customUniforms = {},
    transparent = false,
}: FragmentShaderProps) {
    const meshRef = useRef<THREE.Mesh>(null);
    const materialRef = useRef<THREE.ShaderMaterial>(null);
    const { size } = useThree();
    
    // // Debug: Log when component mounts and size changes (dev only)
    // if (import.meta.env.DEV) {
    //     console.log('[FragmentShader] Render with size:', size.width, 'x', size.height);
    // }

    const resolvedFragmentShader = useMemo(() => {
        if (fragmentShader.toLowerCase().includes('precision ')) {
            return fragmentShader;
        }

        return `precision mediump float;\n${fragmentShader}`;
    }, [fragmentShader]);

    // Combine default uniforms with custom ones - use ref to keep stable
    const uniformsRef = useRef<ShaderUniforms>({
        u_time: { value: 0 },
        u_resolution: { value: [size.width, size.height] },
        u_mouse: { value: [0, 0] },
        ...customUniforms,
    });

    // Update uniforms when customUniforms change (e.g., when texture loads)
    useEffect(() => {
        if (materialRef.current) {
            const { uniforms } = materialRef.current;
            // Update or add custom uniforms
            Object.keys(customUniforms).forEach((key) => {
                const newValue = customUniforms[key].value;
                if (uniforms[key]) {
                    // Only update if value actually changed
                    if (uniforms[key].value !== newValue) {
                        uniforms[key].value = newValue;
                    }
                } else {
                    uniforms[key] = customUniforms[key];
                }
            });
        }
    }, [customUniforms]);

    // Update time uniform on each frame
    useFrame((state) => {
        if (materialRef.current) {
            const { uniforms: shaderUniforms } = materialRef.current;
            if (shaderUniforms.u_time) {
                shaderUniforms.u_time.value = state.clock.elapsedTime;
            }

            if (shaderUniforms.u_resolution) {
                const resolution = shaderUniforms.u_resolution.value as [number, number];
                resolution[0] = state.size.width;
                resolution[1] = state.size.height;
            }
        }
    });

    return (
        <mesh ref={meshRef}>
            <planeGeometry args={[2, 2]} />
            <shaderMaterial
                ref={materialRef}
                vertexShader={vertexShader}
                fragmentShader={resolvedFragmentShader}
                uniforms={uniformsRef.current}
                transparent={transparent}
            />
        </mesh>
    );
}

